import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTimeoutFn } from 'react-use';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { LoanStatus } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Backdrop } from '@sorare/core/src/atoms/loader/Backdrop';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { BrandM, HeadlineXL } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { getComposeTeamRoute } from 'lib/so5';

import { ExtraCardDialog } from './ExtraCardDialog';
import { LoanHeader } from './Header';
import { TeamPreview } from './TeamPreview';
import type {
  LoanStatusQuery,
  LoanStatusQueryVariables,
  LoanTeamSelection_currentUser,
} from './__generated__/index.graphql';

const Root = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: linear-gradient(
      0deg,
      rgba(var(--c-rgb-black), 0.4),
      rgba(var(--c-rgb-black), 0.4)
    ),
    var(--background-image);
  background-size: cover;
  background-repeat: no-repeat;
  -webkit-background-position: center;
  background-position: center;
  padding-bottom: var(--unit);
  isolation: isolate;

  @media ${laptopAndAbove} {
    background-position: center 32%;
    background-size: clamp(100px, 100%, 1300px) auto;
  }
`;

const Content = styled(Vertical).attrs({ center: true, grow: true })`
  padding: 0 var(--double-unit) var(--double-unit) var(--double-unit);
`;

const Footer = styled.div`
  position: sticky;
  bottom: 0;
  padding: var(--unit);
`;

const ButtonReceiver = styled.div`
  width: 100%;

  button {
    width: 100%;
  }

  @media ${laptopAndAbove} {
    width: auto;
  }
`;

const StyledTabBar = styled(TabBar)`
  margin: 0 auto;
`;

const LOAN_STATUS_QUERY = gql`
  query LoanStatusQuery {
    currentUser {
      slug
      loan {
        id
        status
        extraCard {
          slug
          ... on AnyCardInterface {
            slug
            ...ExtraCardDialog_anyCard
          }
        }
      }
    }
  }
  ${ExtraCardDialog.fragments.anyCard}
` as TypedDocumentNode<LoanStatusQuery, LoanStatusQueryVariables>;

export const LoanTeamSelection = ({
  currentUser,
}: {
  currentUser: LoanTeamSelection_currentUser;
}) => {
  const {
    flags: { useLoanFlow },
  } = useFeatureFlags();
  const { generateSportContextPath } = useSportContext();
  const [polling, setPolling] = useState(false);
  const { data } = useQuery(LOAN_STATUS_QUERY, {
    pollInterval: polling ? 1000 : 0,
    skip: !polling,
  });

  useEffect(() => {
    if (polling && data?.currentUser?.loan?.status === LoanStatus.ACTIVE) {
      setPolling(false);
    }
  }, [polling, data?.currentUser?.loan?.status]);

  const isTabletAndAbove = useIsTabletAndAbove();
  const navigate = useNavigate();
  const [selectedLoanTeam, setSelectedLoanTeam] = useState<number>(0);
  const [showExtraCardReveal, setShowExtraCardReveal] =
    useState<boolean>(false);

  const { loanableConfig, loan } = currentUser;

  useTimeoutFn(() => {
    if (polling) {
      setPolling(false);
    }
  }, 30000);

  if (!loan && !loanableConfig) return null;

  const rosters = loanableConfig
    ? loanableConfig.loanableRosters
    : [loan?.loanableRoster].filter(Boolean);
  const nextEligibleLeaderboard =
    loanableConfig?.nextEligibleLeaderboard || loan?.nextEligibleLeaderboard;

  if (rosters.length === 0 || !nextEligibleLeaderboard) return null;
  if (!useLoanFlow || rosters.length < 1 || !nextEligibleLeaderboard)
    return <Navigate to={generateSportContextPath(ANY_SPORT_PLAY)} />;

  const loanAdditionalCard = loan?.extraCard;

  return (
    <Root
      style={
        {
          '--background-image': `url(${nextEligibleLeaderboard.stadiumUrl})`,
        } as CSSProperties
      }
    >
      <LoanHeader
        so5Leaderboard={nextEligibleLeaderboard}
        eligibleUntil={loanableConfig?.eligibleUntil || undefined}
      />
      <Content gap={isTabletAndAbove ? 3 : 2}>
        <BrandM className="text-center">
          <FormattedMessage
            id="LoanTeamSelection.title"
            defaultMessage="Select 1 card to buy,{br}get the other 4 on loan"
            values={{ br: <br /> }}
          />
        </BrandM>
        {rosters.length > 1 && !loan && (
          <StyledTabBar forceMobileLayout value={`${selectedLoanTeam}`}>
            {rosters.map((t, i) => {
              return (
                <Tab
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  value={`${i}`}
                  onClick={() => setSelectedLoanTeam(i)}
                >
                  <FormattedMessage
                    id="LoanTeamSelection.lineup"
                    defaultMessage="Lineup"
                  />{' '}
                  {i + 1}
                </Tab>
              );
            })}
          </StyledTabBar>
        )}
        <Scrollable
          onVisibleItemsChanged={items => {
            setSelectedLoanTeam(items[0]);
          }}
          indexToScroll={selectedLoanTeam}
          itemToDisplay={1}
        >
          {rosters.map((t, i) => (
            <TeamPreview
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              roster={t}
              isSelected={selectedLoanTeam === i}
              onBuySuccess={() => setPolling(true)}
              onBuyConfirmationClose={() => setShowExtraCardReveal(true)}
              hasLoanAssigned={!!loan}
            />
          ))}
        </Scrollable>
      </Content>
      <Footer>
        <Vertical center>
          <ButtonReceiver id="loan-lineup-footer" />
        </Vertical>
      </Footer>
      {!!showExtraCardReveal && !!loanAdditionalCard && (
        <ExtraCardDialog
          card={loanAdditionalCard}
          onDone={() =>
            navigate(
              getComposeTeamRoute({
                leaderboardSlug: nextEligibleLeaderboard.slug,
                eventType: 'loan',
              })
            )
          }
        />
      )}
      {!!polling && (
        <Backdrop>
          <Vertical gap={2}>
            <HeadlineXL>
              <FormattedMessage
                id="LoanTeamSelection.signingLoanPlayed"
                defaultMessage="Signing loan players..."
              />
            </HeadlineXL>
            <LoadingIndicator />
          </Vertical>
        </Backdrop>
      )}
    </Root>
  );
};

LoanTeamSelection.fragments = {
  currentUser: gql`
    fragment LoanTeamSelection_currentUser on CurrentUser {
      slug
      loan {
        id
        status
        loanableRoster {
          id
          ...TeamPreview_loanableRoster
        }
        nextEligibleLeaderboard {
          slug
          stadiumUrl
          ...LoanHeader_so5Leaderboard
        }
        extraCard {
          slug
          ... on AnyCardInterface {
            slug
            ...ExtraCardDialog_anyCard
          }
        }
      }
      loanableConfig {
        eligibleUntil
        loanableRosters {
          id
          ...TeamPreview_loanableRoster
        }
        nextEligibleLeaderboard {
          slug
          stadiumUrl
          ...LoanHeader_so5Leaderboard
        }
      }
    }

    ${LoanHeader.fragments.so5Leaderboard}
    ${TeamPreview.fragments.loanableRoster}
    ${ExtraCardDialog.fragments.anyCard}
  ` as TypedDocumentNode<LoanTeamSelection_currentUser>,
};
